<template>
  <view v-if="props.code !== 'success' && props.code !== 'not-loaded'" class="status-overlay bg-light" :style="[customStyle]">
    <view class="status-content">
      <image v-if="config.image" :src="config.image" class="status-icon" mode="aspectFit" />
      <text v-if="config.text" class="status-title">{{ config.text }}</text>
      <text v-if="config.errorMsg" class="text-24rpx text-red">{{ config.errorMsg }}</text>
      <wd-button v-if="config.showRetry" class="mt-10" @click="handleClick">
        {{ config.retryTitle || '重新加载' }}
      </wd-button>
    </view>
  </view>
</template>

<script setup lang="ts">
import { goLoginPage } from '@/router/back';
import { STATUS_EMPTY_IMAGES } from './status-tips';

const props = defineProps<Props>();

const emit = defineEmits(['retry']);

interface Props {
  code: 'success' | 'no-data' | 'not-loaded' | 'no-login' | 'forbidden' | 'no-network' | 'data-err' | 'system-err' | 'network-err' | 'developing'
  mode?: 'data' | 'search' | 'shopCart' | 'address' | 'order' | 'coupon' | 'favor' | 'history' | 'news' | 'message' | 'comment' | 'list'
  text?: string
  image?: string
  showRetry?: boolean
  retryTitle?: string
  errorCode?: string
  errorMsg?: string
}

const handleClick = () => {
  if (props.code === 'no-login') {
    // 直接进入登录页面（工作台模式 reLaunch 清栈，携带 redirect 回跳）
    goLoginPage();
    return;
  }
  emit('retry');
};

const modes = {
  data: '暂无任何内容',
  search: '没有搜索到结果',
  shopCart: '购物车空空的',
  address: '暂无任何地址',
  order: '订单为空',
  coupon: '暂无任何优惠券',
  favor: '您还未收藏任何内容',
  history: '暂无历史',
  news: '暂无任何通知',
  message: '暂未收到任何消息',
  list: '列表上空空的',
  comment: '暂无评论',
};

const config = computed(() => {
  const configInfo: Props = {
    code: 'no-data',
    mode: 'data',
    text: '',
    errorMsg: props.errorMsg || '',
    image: '',
    showRetry: false,
    retryTitle: '点击重试',
  };

  switch (props.code) {
    case 'no-data':
      configInfo.text = props?.text || modes[props.mode] || '暂无数据';
      configInfo.image = STATUS_EMPTY_IMAGES[props.mode || 'data'];
      break;
    case 'no-login':
      configInfo.text = '您还未登录或登录已过期，请重新登录！';
      configInfo.image = STATUS_EMPTY_IMAGES.nologin;
      configInfo.showRetry = true;
      configInfo.retryTitle = '去登录';
      break;
    case 'forbidden':
      configInfo.text = '您当前权限不足，无法使用该功能！';
      configInfo.image = STATUS_EMPTY_IMAGES.permission;
      break;
    case 'data-err':
      configInfo.text = '当前页面数据丢失，请退出页面重试！';
      configInfo.image = '/static/empty/page.png';
      configInfo.showRetry = true;
      configInfo.retryTitle = '退出页面';
      break;
    case 'no-network':
      configInfo.text = '您当前的网络不可用，请检测网络是否正常！';
      configInfo.image = '/static/empty/wifi.png';
      break;
    case 'system-err':
      configInfo.text = '服务器错误，程序小哥在这抢修中！';
      configInfo.image = '/static/empty/page.png';
      configInfo.showRetry = true;
      break;
    case 'network-err':
      configInfo.text = '当前网络异不给力，请稍后再试！';
      configInfo.image = '/static/empty/wifi.png';
      configInfo.showRetry = true;
      break;
    case 'developing':
      configInfo.text = '当前功能正在开发中，如需使用请前往电脑端！';
      configInfo.image = STATUS_EMPTY_IMAGES.developing;
      configInfo.showRetry = true;
      configInfo.retryTitle = '退出页面';
      break;
  }

  return configInfo;
});

const customStyle = computed(() => {
  return {
    'z-index': props.code === 'no-data' ? 10 : 100,
  };
});
</script>

<style scoped lang="scss">
.status-overlay {
  display: flex;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 200; // 基础层级，高于普通内容

  pointer-events: auto; // 显示声明拦截事件
  touch-action: none; // 移动端：禁止触摸行为
}

.status-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40rpx;
  text-align: center;
  max-width: 600rpx;
  margin-top: 20px;
  gap: 20rpx;
  pointer-events: auto; // 确保子元素（按钮）正常响应点击
}

.status-icon {
  width: 600rpx;
  height: 600rpx;
  //margin-bottom: 30rpx;
  opacity: 0.9;
}

.status-title {
  font-size: 30rpx;
  color: #666666;
}

.status-message {
  font-size: 26rpx;
  color: #999999;
  line-height: 1.6;
}
</style>
